// Sort & Top N panel (schema-mode Data Source tab).
//
// Authors the engine's sqlmodel ranking/limiting keys — no SQL mode needed:
//   • SQLMODEL.ORDER_BY   [{column, direction}]  — result ordering
//   • SQLMODEL.ROW_LIMIT  number                 — fixed row cap (Top-N)
//   • SQLMODEL.ROW_LIMIT_FIXED boolean           — the viewer may NOT raise it
//   • an applyTo:'row_limit' criteria rule       — prompt-driven "Top N"
//     (renders as a numeric prompt in the criteria form; the engine feeds
//     it into the row limit, never the WHERE)
// The backend emits the dialect-correct SQL (LIMIT / FETCH FIRST / TOP)
// for whatever database the visual is bound to.

import { useMemo } from 'react'
import { Plus, X, ArrowDownWideNarrow } from 'lucide-react'
import type {
  CriteriaNode, CriteriaRule, OrderByEntry, VisualModel,
} from './types'
import { criteriaLeaves } from './types'

const selectStyle: React.CSSProperties = {
  padding: '5px 8px', fontSize: 14, borderRadius: 7,
  border: '1px solid var(--color-border-strong)', background: 'var(--color-input-bg)',
  color: 'var(--color-text)', maxWidth: '100%',
}
const inputStyle: React.CSSProperties = { ...selectStyle, width: 90 }
const labelStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)',
}

type LimitMode = 'none' | 'fixed' | 'prompt'

/** The (first) row-limit prompt rule anywhere in the criteria tree. */
export function findRowLimitRule(nodes: CriteriaNode[]): CriteriaRule | null {
  return (
    criteriaLeaves(nodes).find(
      (r) => String((r as Record<string, unknown>).applyTo || '') === 'row_limit',
    ) || null
  )
}

function removeRowLimitRules(nodes: CriteriaNode[]): void {
  for (let i = nodes.length - 1; i >= 0; i--) {
    const n = nodes[i] as Record<string, unknown>
    if (Array.isArray(n.rules) && 'condition' in n) {
      removeRowLimitRules(n.rules as CriteriaNode[])
    } else if (String(n.applyTo || '') === 'row_limit') {
      nodes.splice(i, 1)
    }
  }
}

function nextCriteriaId(nodes: CriteriaNode[]): string {
  const leaves = criteriaLeaves(nodes)
  let n = 1
  while (leaves.some((r) => r.id === `Criteria_${n}`)) n += 1
  return `Criteria_${n}`
}

export default function SortTopNPanel({
  model, onModelChange,
}: {
  model: VisualModel
  onModelChange: (mutator: (m: VisualModel) => void) => void
}) {
  const columns = useMemo(() => {
    const out: { name: string; label: string; isMeasure: boolean }[] = []
    for (const group of model.SQLMODEL.SQLModel?.COLUMNS || []) {
      for (const col of group.COLUMN || []) {
        if (!col?.NAME) continue
        const fn = String(col.FUNCTION || '').toUpperCase()
        out.push({
          name: col.NAME,
          label: String(col.content || col.name || col.NAME),
          isMeasure: !!fn && fn !== 'RANK' && fn !== 'DENSE_RANK' && fn !== 'ROW_NUMBER',
        })
      }
    }
    // Measures first — a Top-N almost always orders by the measure.
    return out.sort((a, b) => Number(b.isMeasure) - Number(a.isMeasure))
  }, [model.SQLMODEL.SQLModel])

  const orderBy: OrderByEntry[] = Array.isArray(model.SQLMODEL.ORDER_BY)
    ? model.SQLMODEL.ORDER_BY : []
  const promptRule = findRowLimitRule(model.SQLMODEL.CRITERIA?.query?.rules || [])
  const fixedLimit = model.SQLMODEL.ROW_LIMIT
  const mode: LimitMode = promptRule ? 'prompt' : (fixedLimit ? 'fixed' : 'none')

  const setOrderBy = (entries: OrderByEntry[]) => onModelChange((m) => {
    if (entries.length) m.SQLMODEL.ORDER_BY = entries
    else delete m.SQLMODEL.ORDER_BY
  })

  const setMode = (next: LimitMode) => onModelChange((m) => {
    const rules = m.SQLMODEL.CRITERIA.query.rules
    removeRowLimitRules(rules)
    delete m.SQLMODEL.ROW_LIMIT
    if (next === 'fixed') {
      m.SQLMODEL.ROW_LIMIT = typeof fixedLimit === 'number' && fixedLimit > 0
        ? fixedLimit : 10
    } else if (next === 'prompt') {
      rules.push({
        id: nextCriteriaId(rules),
        field: 'TOP_N',
        applyTo: 'row_limit',
        COLTYPE: 'number',
        currentFieldType: 'number',
        operator: '<=',
        queryOption: 'P',
        prevOption: 'P',
        customValue: 'FixedValue',
        promptLabel: promptRule?.promptLabel || 'Top N',
        promptVar: 'top_n',
        value: promptRule?.value || String(fixedLimit || 10),
        chipValue: [],
      } as CriteriaRule)
    }
  })

  const patchPromptRule = (patch: Partial<CriteriaRule>) => onModelChange((m) => {
    const rule = findRowLimitRule(m.SQLMODEL.CRITERIA.query.rules)
    if (rule) Object.assign(rule, patch)
  })

  return (
    <div style={{
      marginTop: 16, padding: 12, borderRadius: 11,
      border: '1px solid var(--color-border)',
      background: 'var(--color-bg-secondary)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10,
        fontSize: 13, fontWeight: 700, color: 'var(--color-text-secondary)',
        textTransform: 'uppercase', letterSpacing: 0.4,
      }}>
        <ArrowDownWideNarrow size={13} /> Sort &amp; Top N
      </div>

      {/* ── Order by ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {orderBy.map((entry, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={labelStyle}>{i === 0 ? 'Sort by' : 'then by'}</span>
            <select
              value={entry.column}
              onChange={(e) => setOrderBy(orderBy.map((o, j) => (
                j === i ? { ...o, column: e.target.value } : o
              )))}
              style={{ ...selectStyle, minWidth: 170 }}
            >
              {!columns.some((c) => c.name === entry.column) && entry.column && (
                <option value={entry.column}>{entry.column}</option>
              )}
              {columns.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.label}{c.isMeasure ? ' (measure)' : ''}
                </option>
              ))}
            </select>
            <select
              value={entry.direction}
              onChange={(e) => setOrderBy(orderBy.map((o, j) => (
                j === i ? { ...o, direction: e.target.value as 'ASC' | 'DESC' } : o
              )))}
              style={selectStyle}
            >
              <option value="DESC">highest first (DESC)</option>
              <option value="ASC">lowest first (ASC)</option>
            </select>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setOrderBy(orderBy.filter((_, j) => j !== i))}
              title="Remove this sort"
            >
              <X size={13} />
            </button>
          </div>
        ))}
        <div>
          <button
            className="btn btn-secondary btn-sm"
            disabled={columns.length === 0}
            title={columns.length === 0
              ? 'Add columns to the Layout first — sorting orders on the visual’s columns'
              : 'Sort the result — required for a meaningful Top N'}
            onClick={() => {
              const first = columns[0]
              if (first) setOrderBy([...orderBy, { column: first.name, direction: 'DESC' }])
            }}
          >
            <Plus size={13} /> Add sort
          </button>
        </div>
      </div>

      {/* ── Row limit ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
        <span style={labelStyle}>Row limit</span>
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as LimitMode)}
          style={selectStyle}
        >
          <option value="none">No limit</option>
          <option value="fixed">Fixed (Top N)</option>
          <option value="prompt">Ask at runtime (Top N prompt)</option>
        </select>
        {mode === 'fixed' && (
          <>
            <span style={labelStyle}>keep first</span>
            <input
              type="number" min={1} value={fixedLimit ?? 10}
              onChange={(e) => onModelChange((m) => {
                const n = parseInt(e.target.value, 10)
                if (Number.isFinite(n) && n > 0) m.SQLMODEL.ROW_LIMIT = n
              })}
              style={inputStyle}
            />
            <span style={labelStyle}>rows</span>
          </>
        )}
        {mode === 'prompt' && promptRule && (
          <>
            <span style={labelStyle}>label</span>
            <input
              type="text" value={promptRule.promptLabel || ''}
              onChange={(e) => patchPromptRule({ promptLabel: e.target.value })}
              style={{ ...inputStyle, width: 130 }}
            />
            <span style={labelStyle}>default</span>
            <input
              type="number" min={1} value={promptRule.value || ''}
              onChange={(e) => patchPromptRule({ value: e.target.value })}
              style={inputStyle}
            />
          </>
        )}
      </div>

      {/* ── Whether a viewer may ask for more ──
          Every visual now says when it is showing only part of its result,
          and offers to show more up to the instance ceiling. That is right
          for a list and wrong for a Top-10: "the best ten" turned into "the
          best five hundred" by a reader is a different visual, not more of
          the same one. So an author with a REAL Top-N can pin it. Off by
          default, which is what leaves every visual authored before this
          raisable. */}
      {mode !== 'none' && (
        <label style={{
          display: 'flex', alignItems: 'center', gap: 8, marginTop: 10,
          fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            checked={!!model.SQLMODEL.ROW_LIMIT_FIXED}
            onChange={(e) => onModelChange((m) => {
              if (e.target.checked) m.SQLMODEL.ROW_LIMIT_FIXED = true
              else delete m.SQLMODEL.ROW_LIMIT_FIXED
            })}
          />
          This is a real Top N — don’t let viewers raise it
        </label>
      )}

      <div style={{ fontSize: 13, color: 'var(--color-text-faint)', marginTop: 8 }}>
        {mode === 'prompt'
          ? 'The viewer gets a numeric prompt in the criteria bar; the engine applies it as the row limit for whichever database the visual runs on.'
          : 'Example — “Top 10 suppliers by value”: sort by the value measure (highest first) and limit to 10 rows. The engine writes the right SQL for the bound database.'}
      </div>
    </div>
  )
}
